/* DEC-0045 Queue 34: photo-first review cards and one shared review viewer (every product page; Pet Cabin first).
   Judge.me keeps its data, paging, sort and Glider; this script reads them. The cards and
   "Read more reviews" open the same viewer. Judge.me's own popups remain if this script cannot run. */
(() => {
  'use strict';
  // Lucide Star, Chevron, Close and Play paths, ISC license, matching the approved wireframe.
  const starPath = 'M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z';
  // The arrow chevron: the Lucide shape drawn larger (wireframe v5).
  const arrowPaths = { previous: 'm15 19-7-7 7-7', next: 'm9 19 7-7-7-7' };
  const closePath = 'M18 6 6 18M6 6l12 12';
  const playPath = 'M6 3.5v17l14-8.5z';
  // Judge.me image widths: card photo, viewer photo, thumbnail, sharp on 3x phone screens. Judge.me's image host
  // resizes them and sends WebP, a third to half the bytes of the JPEGs (REQ-MKT-0272-R2: photos were slow on phones).
  const coverWidth = 750;
  const viewerWidth = 900;
  const thumbnailWidth = 200;
  const thumbnailLimit = 4;
  const loadTimeout = 15000;
  // Longest the viewer waits for a review's first photo before showing the review anyway (with a placeholder), so an
  // arrow press or swipe answers at once even on a slow phone connection.
  const photoWait = 250;
  const enhanced = new WeakSet();
  const observed = new WeakSet();
  const interceptedLinks = new WeakSet();
  const cardsById = new Map();
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const tracks = new Set();
  // Judge.me builds the carousel while the section is hidden, so Glider first measures no width.
  // Measure again before paint; the form script's own refresh waits a frame and shows narrow cards once.
  const remeasure = new ResizeObserver(entries => entries.forEach(({ target }) => {
    const glider = target._glider;
    if (glider && target.clientWidth > 0 && target.clientWidth !== glider.containerWidth) glider.refresh();
  }));

  function icon(className, d) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('class', className);
    const path = document.createElementNS(svg.namespaceURI, 'path');
    path.setAttribute('d', d);
    svg.append(path);
    return svg;
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  function button(className, label) {
    const node = element('button', className);
    node.type = 'button';
    if (label) {
      node.setAttribute('aria-label', label);
      node.title = label;
    }
    return node;
  }
  const clean = node => (node?.textContent || '').replace(/\s+/g, ' ').trim();
  // Judge.me puts a "Read more" link in every review body; a review with a rating only (such as many imported Etsy
  // reviews) has nothing else there, so the link is never taken for the review's text.
  function paragraphsOf(review) {
    const body = review.querySelector('.jdgm-rev__body');
    if (!body) return [];
    const paragraphs = [...body.querySelectorAll('p')].map(clean).filter(Boolean);
    if (paragraphs.length) return paragraphs;
    const text = body.cloneNode(true);
    text.querySelectorAll('.jdgm-rev__body-read-more').forEach(link => link.remove());
    return [clean(text)].filter(Boolean);
  }
  // Judge.me's source line for an imported review ("Review written in Etsy"), wherever Judge.me's settings show it.
  function sourceOf(review) {
    const badge = [...review.querySelectorAll('.jdgm-rev__transparency-badge')]
      .find(item => getComputedStyle(item).display !== 'none');
    return badge ? clean(badge) : '';
  }
  function stars(score) {
    const row = element('span', 'rawry-review-stars');
    row.setAttribute('role', 'img');
    row.setAttribute('aria-label', `${score} out of 5 stars`);
    for (let star = 1; star <= 5; star += 1) {
      row.append(icon(star <= score ? 'rawry-review-star rawry-review-star--on' : 'rawry-review-star', starPath));
    }
    return row;
  }
  // Judge.me's Verified badge, on the reviews Judge.me marks verified (REQ-MKT-0272-R2: the cards had dropped it).
  // Judge.me's class gives the label and colours set in Judge.me.
  const verifiedBadge = () => element('span', 'jdgm-rev__buyer-badge rawry-review-verified');
  const isVerified = review => !!review.querySelector('.jdgm-rev__buyer-badge');
  // The reviewer's name, then the badge (David 2026-10-01: the cards need both, as Judge.me's own cards had).
  function byline(className, author, verified) {
    const line = element('p', className);
    line.append(element('span', 'rawry-review-name', author));
    if (verified) line.append(verifiedBadge());
    return line;
  }
  // Carousel photo links point at the review popup (#id), so read the image address from the thumbnail first.
  function photoSource(link, width) {
    const thumbnail = link?.querySelector('img');
    const candidates = [thumbnail?.getAttribute('data-src'), thumbnail?.getAttribute('src'), link?.getAttribute('data-mfp-src'), link?.getAttribute('href')];
    for (const raw of candidates) {
      if (!raw) continue;
      try {
        const url = new URL(raw, location.href);
        if (!/\.(avif|gif|jpe?g|png|webp)$/i.test(url.pathname)) continue;
        url.searchParams.set('width', String(width));
        url.searchParams.set('format', 'webp');
        return url.href;
      } catch (error) {
        // Not a usable address; try the next one.
      }
    }
    return null;
  }
  function photosOf(review) {
    return [...review.querySelectorAll('.jdgm-rev__pic-link')]
      .map(link => ({ type: 'photo', cover: photoSource(link, coverWidth), large: photoSource(link, viewerWidth), thumbnail: photoSource(link, thumbnailWidth) }))
      .filter(photo => photo.cover && photo.large && photo.thumbnail);
  }
  // Judge.me's own player address: its hosted videos play from iframe.mediadelivery.net, links from YouTube.
  const videoHosts = /^(iframe\.mediadelivery\.net|www\.youtube(-nocookie)?\.com)$/;
  function videosOf(review) {
    return [...review.querySelectorAll('.jdgm-vid-player iframe')].map(frame => {
      try {
        const url = new URL(frame.getAttribute('src') || frame.dataset.src || '', location.href);
        if (url.protocol !== 'https:' || !videoHosts.test(url.hostname)) return null;
        if (url.hostname === 'iframe.mediadelivery.net') {
          url.searchParams.set('autoplay', 'true');
          url.searchParams.set('preload', 'true');
          url.searchParams.set('muted', 'false');
        } else {
          url.searchParams.set('autoplay', '1');
        }
        return { type: 'video', src: url.href };
      } catch (error) {
        return null;
      }
    }).filter(Boolean);
  }

  /* Thumbnails: one row component for the cards and the viewer. */
  function buildThumbnails(media, author, selected, onSelect, limit = thumbnailLimit) {
    const row = element('div', 'rawry-review-thumbnails');
    if (media.length < 2) {
      row.setAttribute('aria-hidden', 'true');
      return row;
    }
    row.setAttribute('role', 'group');
    row.setAttribute('aria-label', `Photos from ${author}`);
    const photoCount = media.filter(item => item.type === 'photo').length;
    const buttons = media.slice(0, limit).map((item, index) => {
      const label = item.type === 'video' ? 'Show video' : `Show photo ${index + 1} of ${photoCount}`;
      const thumb = button('rawry-review-thumbnail', label);
      thumb.setAttribute('aria-pressed', String(index === selected));
      if (item.type === 'video') {
        thumb.classList.add('rawry-review-thumbnail--video');
        thumb.append(icon('rawry-review-play', playPath));
      } else {
        const image = element('img');
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.src = item.thumbnail;
        thumb.append(image);
      }
      thumb.addEventListener('click', event => {
        event.stopPropagation();
        buttons.forEach(other => other.setAttribute('aria-pressed', String(other === thumb)));
        onSelect(index);
      });
      return thumb;
    });
    row.append(...buttons);
    row.rawrySelect = index => buttons.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
    return row;
  }

  /* Cards (steps 1 and 2). */
  function buildCover(photos, product, author, open) {
    if (!photos.length) {
      // Photo-free reviews stay in the carousel; their card layout is a separate decision.
      const empty = element('div', 'rawry-review-card__cover');
      empty.setAttribute('aria-hidden', 'true');
      return { cover: empty, show: () => {} };
    }
    const cover = button('rawry-review-card__cover');
    cover.setAttribute('aria-haspopup', 'dialog');
    cover.setAttribute('aria-label', `Open photos and review by ${author}`);
    cover.removeAttribute('title');
    const photo = element('img', 'rawry-review-card__photo');
    const fallback = element('span', 'rawry-review-card__fallback', 'Photo unavailable');
    fallback.hidden = true;
    photo.alt = `${product} photographed by ${author}`;
    photo.loading = 'lazy';
    photo.decoding = 'async';
    photo.addEventListener('error', () => {
      photo.hidden = true;
      fallback.hidden = false;
    });
    photo.src = photos[0].cover;
    cover.append(photo, fallback);
    cover.addEventListener('click', () => open(cover));
    let request = 0;
    // Load the chosen photo first, then swap, so the card never flashes empty.
    const show = index => {
      const source = photos[index]?.cover;
      if (!source) return;
      const ticket = ++request;
      const next = new Image();
      next.src = source;
      next.decode().catch(() => {}).then(() => {
        if (ticket !== request) return;
        photo.hidden = false;
        fallback.hidden = true;
        photo.src = source;
      });
    };
    return { cover, show };
  }

  function buildCard(slide, product, index, count) {
    if (enhanced.has(slide)) return true;
    const nativeOpen = slide.querySelector('.jdgm-rev__read-full');
    const score = Number(slide.querySelector('.jdgm-rev__rating')?.dataset.score);
    if (!nativeOpen || !(score >= 1 && score <= 5)) return false;
    enhanced.add(slide);
    const id = slide.dataset.reviewId;
    const author = clean(slide.querySelector('.jdgm-rev__author'));
    const title = clean(slide.querySelector('.jdgm-rev__title'));
    const photos = photosOf(slide);
    // Opens the shared viewer at this review; Judge.me's popup if the viewer cannot open.
    const open = opener => openViewer({ id, photo: Number(slide.dataset.rawryPhoto) || 0 }, opener).catch(() => nativeOpen.click());
    const { cover, show } = buildCover(photos, product, author, open);

    const openText = button('rawry-review-card__open');
    openText.setAttribute('aria-haspopup', 'dialog');
    openText.setAttribute('aria-label', title ? `Read full review by ${author}: ${title}` : `Read full review by ${author}`);
    if (title) openText.title = title;
    else openText.removeAttribute('title');
    // Reviews without a title (such as imported Etsy reviews) start with their text, one line longer.
    const text = paragraphsOf(slide).join(' ');
    if (title) openText.append(element('span', 'rawry-review-card__title', title));
    else slide.classList.add('rawry-review-card--untitled');
    openText.append(
      element('span', 'rawry-review-card__teaser', text),
      element('span', 'rawry-review-card__action', 'Read full review')
    );
    openText.addEventListener('click', () => open(openText));
    const select = photoIndex => {
      slide.dataset.rawryPhoto = String(photoIndex);
      thumbnails.rawrySelect?.(photoIndex);
      show(photoIndex);
    };
    // Changes the card photo only: no viewer, and the cards stay where they are.
    const thumbnails = buildThumbnails(photos, author, 0, select);
    const caption = element('div', 'rawry-review-card__caption');
    caption.append(stars(score), byline('rawry-review-card__author', author, isVerified(slide)));
    // A rating with no words has nothing more to read: its source line, if any, takes the text's place.
    const source = sourceOf(slide);
    if (title || text) caption.append(openText);
    else if (source) caption.append(element('p', 'rawry-review-source', source));
    caption.append(thumbnails);

    slide.setAttribute('role', 'group');
    slide.setAttribute('aria-roledescription', 'slide');
    slide.setAttribute('aria-label', `Review ${index + 1} of ${count}`);
    slide.classList.add('rawry-review-card');
    slide.dataset.rawryPhoto = '0';
    slide.append(cover, caption);
    cardsById.set(id, { slide, select, photoCount: photos.length });
    return true;
  }

  // Arrows stay where Judge.me's Glider put them (its selectors find them there); CSS gives each its own grid column.
  function labelControls(row) {
    [['prev', 'Previous review', arrowPaths.previous, 'ArrowLeft', '←'], ['next', 'Next review', arrowPaths.next, 'ArrowRight', '→']].forEach(([side, label, d, key, glyph]) => {
      const arrow = row.querySelector(`:scope > .jdgm-rev-carousel-${side}`);
      if (!arrow || arrow.querySelector('.rawry-review-chevron')) return;
      arrow.classList.add('rawry-review-arrow');
      arrow.setAttribute('aria-label', label);
      arrow.setAttribute('aria-keyshortcuts', key);
      arrow.title = `${label} (${glyph} key)`;
      arrow.append(icon('rawry-review-chevron', d));
    });
    bindKeys(row);
    const link = row.parentElement?.querySelector(':scope > .jdgm-row-readmore > a');
    if (!link) return;
    if (link.textContent !== 'Read more reviews') link.textContent = 'Read more reviews';
    if (interceptedLinks.has(link)) return;
    interceptedLinks.add(link);
    link.setAttribute('aria-haspopup', 'dialog');
    // Runs before Judge.me's handler on the same link; falls back to Judge.me's list on failure.
    link.addEventListener('click', event => {
      event.preventDefault();
      event.stopImmediatePropagation();
      openViewer({ id: firstVisibleId(row) }, link).catch(() => {
        interceptedLinks.delete(link);
        window.jdgm?.$?.(link).trigger('click');
      });
    }, true);
  }
  // Arrow keys (David 2026-09-29): once focus is in the review cards (a click or Tab inside them), left and right do
  // what the arrows do. Text fields and menus keep their own keys; the product gallery above is not affected.
  const inField = target => target.closest?.('input, select, textarea, [contenteditable]');
  const arrowKey = event => (event.key === 'ArrowLeft' || event.key === 'ArrowRight') && !event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey && !inField(event.target);
  // The cards take the keys only while at least half of the row is on screen: scrolled away, the keys are left for
  // what is on screen, such as the product photos (David 2026-09-30).
  const onScreen = element => {
    const box = element.getBoundingClientRect();
    return Math.min(box.bottom, innerHeight) - Math.max(box.top, 0) >= Math.min(box.height, innerHeight) / 2;
  };
  // Focus inside a card stays at the same place on screen: it moves to the same control on the card that slid in.
  // Judge.me makes a card inert until half of it is in view, so the incoming card is released for focus first
  // (Judge.me's own observer would release it halfway through the slide anyway).
  function followFocus(from, card) {
    card.inert = false;
    card.removeAttribute('aria-hidden');
    const same = from.matches('.rawry-review-thumbnail') ? `.rawry-review-thumbnail:nth-child(${[...from.parentElement.children].indexOf(from) + 1})`
      : from.matches('.rawry-review-card__open') ? '.rawry-review-card__open' : '.rawry-review-card__cover';
    const target = card.querySelector(same);
    (target && getComputedStyle(target).display !== 'none' ? target : card.querySelector('.rawry-review-card__open'))?.focus({ preventScroll: true });
  }
  function bindKeys(row) {
    if (row.dataset.rawryKeys) return;
    row.dataset.rawryKeys = 'on';
    // A click anywhere in the row gives it focus, so the keys work after any click: Safari does not focus buttons on
    // click, and Glider's mouse drag cancels the browser's own focus change inside the cards.
    row.tabIndex = -1;
    let press = null;
    row.addEventListener('pointerdown', event => {
      press = { x: event.clientX, y: event.clientY };
      if (!row.contains(document.activeElement)) row.focus({ preventScroll: true });
    }, true);
    // A drag that ends over a card is not a click on it: Glider only stops the click after drags longer than 300ms.
    row.addEventListener('click', event => {
      if (!event.detail || !press || Math.hypot(event.clientX - press.x, event.clientY - press.y) < 6) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
    const onKey = event => {
      if (!arrowKey(event) || !onScreen(row)) return;
      const loop = loopRow(row);
      if (!loop) return;
      event.preventDefault();
      loop.step(event.key === 'ArrowRight' ? 1 : -1, event.target);
    };
    row.addEventListener('keydown', onKey);
    row.parentElement?.querySelector(':scope > .jdgm-row-readmore')?.addEventListener('keydown', onKey);
  }
  /* Looping (David 2026-09-29): the cards never end. Glider can only rewind, so the arrows and keys are taken over.
     While no slide is in flight, cards move from one end of Glider's track to the other out of sight, keeping spare
     cards on both sides: no copies, every card is the real one. A press during a slide redirects it; a press with no
     spare card left on that side waits for the row to settle. After a swipe or drag, Glider settles on the nearest
     card. A resize keeps the same card first, even mid-slide. On touch screens the browser snaps card by card during a
     swipe (David 2026-10-01); snapping steps aside while the script moves the row itself ("rawry-review-sliding"). */
  const loops = new WeakMap();
  // A slide that never reports its end (for example cut short by a refresh) stops counting after this long.
  const slideLimit = 2000;
  function loopRow(row) {
    const track = row.querySelector(':scope > .jdgm-rev-carousel');
    const glider = track?._glider;
    if (!glider) return null;
    if (loops.has(track)) return loops.get(track);
    // Glider recounts its own slide from the scroll position on every frame, so the loop keeps the target itself, and
    // the card shown first (or the one a slide is going to) by element, since moving cards and resizing change numbers.
    const loop = { moving: 0, target: 0, queued: [], timer: 0, anchor: null, flights: 0 };
    const columns = () => Math.max(1, Math.floor(glider.opt.slidesToShow));
    const spare = () => glider.slides.length - columns();
    const ready = () => glider.itemWidth > 0 && track.clientWidth > 0;
    const shown = () => Math.round(track.scrollLeft / glider.itemWidth);
    const moving = () => loop.moving && performance.now() - loop.moving < slideLimit;
    // A new Glider slide cuts the last one short, and that one then jumps to its own end. Count the slides in flight,
    // so cards move between the ends only when none is.
    const sliding = on => {
      if (on) row.classList.add('rawry-review-sliding');
      else if (!loop.flights) row.classList.remove('rawry-review-sliding');
    };
    const scrollTo = glider.scrollTo;
    glider.scrollTo = function (x, duration, done) {
      loop.flights += 1;
      sliding(true);
      return scrollTo.call(this, x, duration, function () {
        loop.flights -= 1;
        sliding(false);
        if (done) done.call(this);
      });
    };
    // Only while still: move cards between the ends, keeping the same cards in view.
    function rotate(shift) {
      const first = shown();
      sliding(true);
      for (let i = 0; i < Math.abs(shift); i += 1) {
        if (shift > 0) glider.track.append(glider.track.firstElementChild);
        else glider.track.prepend(glider.track.lastElementChild);
      }
      track.scrollLeft = (first - shift) * glider.itemWidth;
      glider.updateControls();
      requestAnimationFrame(() => sliding(false));
    }
    // The cards in view sit in the middle of the track, so either arrow has spare cards to show.
    function balance() {
      if (!ready() || spare() < 1) return;
      const shift = shown() - Math.floor(spare() / 2);
      if (shift) rotate(shift);
    }
    function settled() {
      if (loop.flights) return;
      sliding(false);
      loop.moving = 0;
      if (!ready() || Math.abs(track.scrollLeft / glider.itemWidth - shown()) > 0.02) return;
      balance();
      loop.anchor = glider.slides[shown()];
      const next = loop.queued.shift();
      if (next) step(next, row.contains(document.activeElement) ? document.activeElement : null);
    }
    // One card either way. `from` is a focused control: in a card, focus follows to the same control on the card
    // that takes its place on screen.
    function step(direction, from = null) {
      if (!ready() || spare() < 1) return;
      let first = moving() ? loop.target : shown();
      if (first + direction < 0 || first + direction > spare()) {
        if (moving() || loop.flights) {
          loop.queued.push(direction);
          return;
        }
        rotate(direction);
        first -= direction;
      }
      const card = from?.closest?.('.rawry-review-card');
      const slot = card ? [...glider.slides].indexOf(card) - first : -1;
      loop.moving = performance.now();
      loop.target = first + direction;
      loop.anchor = glider.slides[loop.target];
      glider.scrollItem(loop.target);
      const incoming = slot >= 0 && slot < columns() ? glider.slides[first + direction + slot] : null;
      if (incoming && incoming !== card) followFocus(from, incoming);
    }
    // The arrows: run before Glider's own handler on them and stop it.
    row.addEventListener('click', event => {
      const arrow = event.target.closest?.('.jdgm-rev-carousel-prev, .jdgm-rev-carousel-next');
      if (!arrow || arrow.parentElement !== row) return;
      event.preventDefault();
      event.stopPropagation();
      step(arrow.classList.contains('jdgm-rev-carousel-next') ? 1 : -1);
    }, true);
    // The last slide in flight settles the row, whether it was an arrow's or Glider's own after a swipe.
    track.addEventListener('glider-animated', settled);
    const later = () => {
      clearTimeout(loop.timer);
      loop.timer = setTimeout(() => {
        if (!moving()) settled();
      }, 300);
    };
    track.addEventListener('scroll', later, { passive: true });
    // A resize makes Glider re-measure and put back its own slide number, which after a big resize (a phone turned)
    // or one mid-slide can be another card. After Glider's jump, put back the card that was first, or aim the slide
    // at the card it was going to.
    track.addEventListener('glider-refresh', () => {
      requestAnimationFrame(() => {
        const index = [...glider.slides].indexOf(loop.anchor);
        if (index < 0 || !ready()) return;
        if (moving()) {
          loop.target = index;
          glider.scrollItem(index);
        } else if (Math.abs(track.scrollLeft - index * glider.itemWidth) > 0.5) {
          track.scrollLeft = index * glider.itemWidth;
          glider.updateControls();
        }
      });
      later();
    });
    glider.setOption({ scrollLock: true, scrollLockDelay: 150 }, true);
    loop.step = step;
    loops.set(track, loop);
    later();
    return loop;
  }
  function firstVisibleId(row) {
    const track = row.querySelector('.jdgm-rev-carousel');
    const glider = track?._glider;
    const slide = glider ? glider.slides[Math.round(track.scrollLeft / glider.itemWidth)] : track?.querySelector('.jdgm-rev');
    return slide?.dataset.reviewId;
  }

  // No slide animation for visitors who ask for reduced motion (the wireframe does the same).
  function applyMotion(track) {
    const glider = track._glider;
    if (!glider) return;
    if (track.rawryDuration === undefined) track.rawryDuration = glider.opt.duration;
    const duration = reducedMotion.matches ? 0 : track.rawryDuration;
    if (glider.opt.duration !== duration) glider.setOption({ duration }, true);
  }

  function enhanceRow(row, product) {
    const slides = [...row.querySelectorAll('.jdgm-rev-carousel .jdgm-rev')];
    const built = slides.filter((slide, index) => buildCard(slide, product, index, slides.length));
    if (!built.length) return;
    labelControls(row);
    const track = row.querySelector('.jdgm-rev-carousel');
    if (track?._glider && !tracks.has(track)) {
      tracks.add(track);
      remeasure.observe(track);
    }
    if (track) applyMotion(track);
    loopRow(row);
    if (row.classList.contains('rawry-review-carousel')) return;
    row.classList.add('rawry-review-carousel');
    // New card widths: let Glider measure again.
    track?._glider?.refresh();
  }

  /* Reviews feed: Judge.me's own list and pager (in its hidden Read More content), kept by position so the viewer
     can loop (David 2026-09-29): past the last review comes the first, and before the first, the last. */
  const feed = { sort: null, defaultSort: null, reviews: [], page: 0, perPage: 0, lastPage: 0, total: 0, loading: null };
  const widget = () => document.querySelector('.our-reviewyyyy .jdgm-rev-widg');
  const nativeList = () => widget()?.querySelector('.jdgm-rev-widg__popup-content .jdgm-rev-widg__body');
  const nativeSort = () => widget()?.querySelector('.jdgm-rev-widg__popup-content select.jdgm-sort-dropdown');

  function readReview(review) {
    const score = Number(review.querySelector('.jdgm-rev__rating')?.dataset.score);
    return {
      id: review.dataset.reviewId,
      score: score >= 1 && score <= 5 ? score : 0,
      author: clean(review.querySelector('.jdgm-rev__author')),
      verified: isVerified(review),
      source: sourceOf(review),
      title: clean(review.querySelector('.jdgm-rev__title')),
      paragraphs: paragraphsOf(review),
      media: [...photosOf(review), ...videosOf(review)]
    };
  }
  // Stores the page Judge.me shows now, each review at its place in the whole list.
  function readPage() {
    const list = nativeList();
    if (!list) throw new Error('No Judge.me review list');
    const page = Number(list.querySelector('.jdgm-paginate__page.jdgm-curt')?.dataset.page) || 1;
    const pages = [...list.querySelectorAll('.jdgm-paginate [data-page]')].map(link => Number(link.dataset.page)).filter(Boolean);
    const reviews = [...list.querySelectorAll('.jdgm-rev-widg__reviews > .jdgm-rev')].map(readReview).filter(review => review.id);
    feed.page = page;
    feed.lastPage = Math.max(feed.lastPage, page, ...pages);
    if (page < feed.lastPage || !feed.perPage) feed.perPage = Math.max(feed.perPage, reviews.length);
    if (!feed.perPage) throw new Error('No reviews on the page');
    reviews.forEach((review, offset) => { feed.reviews[(page - 1) * feed.perPage + offset] = review; });
    // The last page tells the exact total; before that, Judge.me's own count (all reviews in every sort but Only Pictures).
    if (page === feed.lastPage) feed.total = (page - 1) * feed.perPage + reviews.length;
    return page;
  }
  function resetFeed(sort) {
    Object.assign(feed, { sort, reviews: [], page: 0, perPage: 0, lastPage: 0, total: 0 });
    readPage();
    if (!feed.total && sort !== 'with-pictures') feed.total = Number(widget()?.dataset.numberOfReviews) || 0;
  }
  // Judge.me scrolls the page when it loads a list page; keep the page behind the viewer still.
  function holdScroll() {
    const top = window.scrollY;
    const keep = () => {
      if (Math.abs(window.scrollY - top) > 1) window.scrollTo({ top, behavior: 'instant' });
    };
    window.addEventListener('scroll', keep, { passive: true });
    return () => {
      keep();
      window.jdgm?.$?.('html, body').stop(true);
      setTimeout(() => {
        keep();
        window.removeEventListener('scroll', keep);
      }, 900);
    };
  }
  // Resolves when Judge.me has replaced the list after an action (its content can repeat, so any reload counts).
  function reloadList(action) {
    const list = nativeList();
    if (!list) return Promise.reject(new Error('No Judge.me review list'));
    const release = holdScroll();
    return new Promise((resolve, reject) => {
      let settle = 0;
      const done = () => {
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      };
      const observer = new MutationObserver(records => {
        if (!records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('.jdgm-rev') || node.querySelector?.('.jdgm-rev'))))) return;
        clearTimeout(settle);
        settle = setTimeout(done, 150);
      });
      observer.observe(list, { childList: true, subtree: true });
      const timer = setTimeout(() => {
        observer.disconnect();
        reject(new Error('Judge.me did not load the reviews'));
      }, loadTimeout);
      action();
    }).finally(release);
  }
  // Judge.me's pager links carry their page number (first, previous, numbers, next, last): click the one for the
  // page wanted, or the one closest to it, until that page shows.
  function loadPage(target) {
    if (feed.loading) return feed.loading.then(() => loadPage(target));
    const hop = () => {
      const list = nativeList();
      if (!list) return Promise.reject(new Error('No Judge.me review list'));
      if (feed.page === target) return Promise.resolve(true);
      const closer = [...list.querySelectorAll('.jdgm-paginate [data-page]:not(.jdgm-curt)')]
        .filter(link => Math.abs(Number(link.dataset.page) - target) < Math.abs(feed.page - target))
        .sort((a, b) => Math.abs(Number(a.dataset.page) - target) - Math.abs(Number(b.dataset.page) - target))[0];
      if (!closer) return Promise.reject(new Error('No pager link towards that page'));
      return reloadList(() => closer.click()).then(() => {
        readPage();
        return hop();
      });
    };
    feed.loading = hop().finally(() => {
      feed.loading = null;
    });
    return feed.loading;
  }
  // The viewer has no sort (David 2026-09-29); this only puts Judge.me's default order back if it changed.
  function setSort(value) {
    const select = nativeSort();
    if (!select) return Promise.reject(new Error('No Judge.me sort'));
    if (select.value === value) {
      if (feed.sort !== value) resetFeed(value);
      return Promise.resolve();
    }
    feed.loading = reloadList(() => {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }).then(() => resetFeed(value)).finally(() => {
      feed.loading = null;
    });
    return feed.loading;
  }
  // The main image of the variant the shopper has selected (theme.liquid lists one per variant), for reviews without photos.
  let productImages;
  function productImage() {
    if (productImages === undefined) {
      try {
        productImages = JSON.parse(document.getElementById('RawryReviewProductImages')?.textContent || 'null');
      } catch (error) {
        productImages = null;
      }
    }
    if (!productImages) return null;
    const variant = document.querySelector('#MainContent form[action*="/cart/add"] [name="id"]')?.value || new URL(location.href).searchParams.get('variant');
    const raw = productImages.variants?.[variant] || productImages.fallback;
    try {
      return raw ? new URL(raw, location.href).href : null;
    } catch (error) {
      return null;
    }
  }

  /* Viewer (wireframe v5: one native dialog for both entry points; one arrow pair, review to review, looping). */
  const viewer = { dialog: null, index: 0, photo: 0, opener: null, busy: false, error: '', stage: null, thumbnails: null, rendered: null, ticket: 0, carried: false, queued: 0 };

  function buildViewer() {
    const root = document.querySelector('.our-reviewyyyy');
    if (!root) throw new Error('No review section');
    if (viewer.dialog?.isConnected) return viewer.dialog;
    const dialog = element('dialog', 'rawry-review-viewer');
    dialog.setAttribute('aria-labelledby', 'rawry-review-viewer-title');
    const header = element('header', 'rawry-review-viewer__header');
    const heading = element('h2', 'rawry-review-viewer__title', 'Customer Review');
    heading.id = 'rawry-review-viewer-title';
    const close = button('rawry-review-viewer__close', 'Close reviews');
    close.append(icon('rawry-review-icon', closePath));
    header.append(heading, close);
    const content = element('div', 'rawry-review-viewer__content');
    const footer = element('footer', 'rawry-review-viewer__footer');
    footer.setAttribute('aria-label', 'Browse reviews');
    const previousReview = button('rawry-review-arrow rawry-review-viewer__review-previous', 'Previous review');
    previousReview.title = 'Previous review (← key)';
    previousReview.setAttribute('aria-keyshortcuts', 'ArrowLeft');
    previousReview.append(icon('rawry-review-chevron', arrowPaths.previous));
    const position = element('p', 'rawry-review-viewer__position');
    position.setAttribute('aria-live', 'polite');
    position.setAttribute('aria-atomic', 'true');
    const nextReview = button('rawry-review-arrow rawry-review-viewer__review-next', 'Next review');
    nextReview.title = 'Next review (→ key)';
    nextReview.setAttribute('aria-keyshortcuts', 'ArrowRight');
    nextReview.append(icon('rawry-review-chevron', arrowPaths.next));
    footer.append(previousReview, position, nextReview);
    dialog.append(header, content, footer);
    root.append(dialog);

    close.addEventListener('click', () => dialog.close());
    // A click on the backdrop lands on the dialog itself, outside its box.
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
    });
    // The second press of a double click on a card or Read more lands in the view the first press opened. It must not
    // press what is under it: Close, an arrow, a thumbnail or the backdrop.
    dialog.addEventListener('mousedown', event => { if (event.detail < 2) viewer.carried = false; }, true);
    dialog.addEventListener('click', event => {
      if (!viewer.carried || event.detail < 2) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('rawry-review-viewer-open');
      const opener = viewer.opener;
      viewer.opener = null;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
    previousReview.addEventListener('click', () => moveReview(-1));
    nextReview.addEventListener('click', () => moveReview(1));
    // A sideways swipe on the review goes to the next or previous one, as the arrows do (David 2026-10-01). Up and down
    // still scroll the review.
    let touch = null;
    content.addEventListener('touchstart', event => {
      touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    content.addEventListener('touchcancel', () => { touch = null; }, { passive: true });
    content.addEventListener('touchend', event => {
      const end = event.changedTouches[0];
      if (!touch || !end || event.touches.length) return;
      const dx = end.clientX - touch.x;
      const dy = end.clientY - touch.y;
      touch = null;
      if (Math.abs(dx) >= 50 && Math.abs(dx) > 1.5 * Math.abs(dy)) moveReview(dx < 0 ? 1 : -1);
    }, { passive: true });
    Object.assign(viewer, { dialog, content, close, previousReview, nextReview, position, rendered: null, stage: null, thumbnails: null });
    return dialog;
  }

  // Resolves true once the task is done. The arrows stay live (a disabled button drops focus); moves wait while busy.
  function runBusy(task) {
    viewer.busy = true;
    viewer.error = '';
    viewer.content.setAttribute('aria-busy', 'true');
    syncControls();
    return task().then(() => true, () => {
      viewer.error = 'Could not load more reviews. Try again.';
      return false;
    }).finally(() => {
      viewer.busy = false;
      viewer.content.removeAttribute('aria-busy');
      syncControls();
    });
  }

  function mediaLabel(item, media) {
    const sameType = media.filter(entry => entry.type === item.type);
    return `${item.type === 'video' ? 'video' : 'photo'} ${sameType.indexOf(item) + 1} of ${sameType.length}`;
  }

  // The photo box has a declared height when the review has thumbnails, so they never move while you switch photos;
  // with one photo or none it fits the photo (same maximum). The photo sits at the top of its box.
  // Resolves once the media is in the box; a photo goes in only after it has loaded. Until then a new review's box
  // shows a light placeholder with a spinner (CSS), so the review itself never waits for its photo.
  function buildStage() {
    const stage = element('div', 'rawry-review-viewer__stage');
    let request = 0;
    stage.rawryShow = (review, item, product) => new Promise(resolve => {
      const ticket = ++request;
      const place = node => {
        if (ticket === request) {
          stage.classList.toggle('rawry-review-viewer__stage--single', review.media.length <= 1 && item?.type !== 'video');
          stage.replaceChildren(node);
        }
        resolve();
      };
      const framed = (source, alt, label) => {
        const frame = element('figure', 'rawry-review-viewer__frame');
        const photo = element('img', 'rawry-review-viewer__photo');
        photo.alt = alt;
        photo.decoding = 'async';
        photo.addEventListener('error', () => frame.replaceWith(element('p', 'rawry-review-viewer__empty', 'Photo unavailable')), { once: true });
        photo.src = source;
        frame.append(photo);
        if (label) {
          const tag = element('span', 'rawry-review-viewer__label', label);
          tag.setAttribute('aria-hidden', 'true');
          frame.append(tag);
        }
        if (photo.complete) place(frame);
        else photo.decode().catch(() => {}).then(() => place(frame));
      };
      if (!item) {
        // A review without photos shows the product's main variant image, marked so it is not taken for the reviewer's photo.
        const source = productImage();
        if (source) framed(source, `${product} product photo; this review has no customer photos`, 'Product photo');
        else place(element('p', 'rawry-review-viewer__empty', 'No photos with this review'));
      } else if (item.type === 'video') {
        // The video player loads only after a tap: no autoplay, nothing requested before that.
        const play = button('rawry-review-viewer__play', `Play video from ${review.author}`);
        play.append(icon('rawry-review-play', playPath), element('span', '', 'Play video'));
        play.addEventListener('click', () => {
          const frame = element('iframe', 'rawry-review-viewer__video');
          frame.src = item.src;
          frame.title = `Video from ${review.author}`;
          frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
          frame.allowFullscreen = true;
          play.replaceWith(frame);
          frame.focus();
        });
        place(play);
      } else {
        framed(item.large, `${product} photographed by ${review.author}, ${mediaLabel(item, review.media)}`);
      }
    });
    return stage;
  }

  // The first photo of the reviews either side (or the product photo for one without photos) loads ahead, so an arrow
  // press or swipe shows the next review's photo at once.
  function preloadAround(index) {
    [1, -1].forEach(step => {
      const review = feed.total && feed.reviews[(index + step + feed.total) % feed.total];
      const first = review?.media[0];
      const url = !review ? null : first ? (first.type === 'photo' ? first.large : null) : productImage();
      if (url) new Image().src = url;
    });
  }

  // The count and focus move at once. Another review is built aside and swapped in once its first photo has loaded,
  // or after a quarter second with a placeholder in the photo box.
  function showReview(index, photo, focusTarget) {
    const review = feed.reviews[index];
    if (!review) return;
    // A review on screen replaces an earlier "Could not load" message; the next press past it tries again.
    viewer.error = '';
    const product = document.querySelector('.our-reviewyyyy')?.dataset.productTitle || '';
    viewer.index = index;
    viewer.photo = Math.max(0, Math.min(photo, review.media.length - 1));
    const ticket = ++viewer.ticket;
    syncControls();
    const target = typeof focusTarget === 'string' ? viewer.dialog.querySelector(focusTarget) : focusTarget;
    if (target?.isConnected) target.focus({ preventScroll: true });
    if (viewer.rendered === review.id) {
      viewer.thumbnails?.rawrySelect?.(viewer.photo);
      viewer.stage.rawryShow(review, review.media[viewer.photo], product);
      return;
    }
    const detail = element('article', 'rawry-review-viewer__detail');
    detail.dataset.review = review.id;
    const media = element('div', 'rawry-review-viewer__media');
    const stage = buildStage();
    media.append(stage);
    const thumbnails = review.media.length > 1 ? buildThumbnails(review.media, review.author, viewer.photo, choice => selectPhoto(choice), Infinity) : null;
    if (thumbnails) media.append(thumbnails);
    const copy = element('div', 'rawry-review-viewer__copy');
    if (review.score) copy.append(stars(review.score));
    if (review.title) copy.append(element('h3', 'rawry-review-viewer__heading', review.title));
    copy.append(byline('rawry-review-viewer__author', review.author, review.verified));
    if (review.source) copy.append(element('p', 'rawry-review-source', review.source));
    if (review.paragraphs.length) {
      const body = element('div', 'rawry-review-viewer__body');
      review.paragraphs.forEach(text => body.append(element('p', '', text)));
      copy.append(body);
    }
    detail.append(media, copy);
    const ready = stage.rawryShow(review, review.media[viewer.photo], product);
    Promise.race([ready, new Promise(done => setTimeout(done, photoWait))]).then(() => {
      if (ticket !== viewer.ticket) return;
      Object.assign(viewer, { rendered: review.id, stage, thumbnails });
      viewer.content.replaceChildren(detail);
      viewer.content.scrollTop = 0;
    });
    preloadAround(index);
  }

  // Also keeps the matching card on the chosen photo (the wireframe syncs both).
  function selectPhoto(choice) {
    const review = feed.reviews[viewer.index];
    showReview(viewer.index, choice);
    const card = cardsById.get(review.id);
    if (card && review.media[choice]?.type === 'photo' && choice < card.photoCount) card.select(choice);
  }

  function syncControls() {
    if (!viewer.dialog) return;
    const review = feed.reviews[viewer.index];
    if (viewer.busy || viewer.error || !review) {
      viewer.position.textContent = viewer.error || 'Loading reviews…';
      return;
    }
    viewer.position.textContent = feed.total ? `Review ${viewer.index + 1} of ${feed.total}` : `Review ${viewer.index + 1}`;
  }

  // Loops both ways. A review not loaded yet comes from Judge.me's pager first; each review opens at its first photo.
  function goTo(index, focus) {
    if (!feed.total) {
      // Only when Judge.me gives no count (Only Pictures as the default order): its last page tells the total.
      runBusy(() => loadPage(feed.lastPage)).then(ok => ok && feed.total && goTo(index, focus));
      return;
    }
    const target = (index + feed.total) % feed.total;
    if (feed.reviews[target]) {
      showReview(target, 0, focus);
      return;
    }
    runBusy(() => loadPage(Math.floor(target / feed.perPage) + 1)).then(ok => {
      if (ok && feed.reviews[target]) showReview(target, 0, focus);
      const queued = viewer.queued;
      viewer.queued = 0;
      if (ok && queued && viewer.dialog.open) moveReview(queued);
    });
  }
  // A press while a page of reviews loads is kept and made once it has loaded, so quick presses all count.
  function moveReview(direction) {
    if (viewer.busy) {
      viewer.queued += direction;
      return;
    }
    goTo(viewer.index + direction, `.rawry-review-viewer__review-${direction > 0 ? 'next' : 'previous'}`);
  }

  // Cards follow Judge.me's default order, so the viewer returns to it before opening.
  async function openViewer(start, opener) {
    const dialog = buildViewer();
    if (!feed.defaultSort) {
      feed.defaultSort = nativeSort()?.value || null;
      resetFeed(feed.defaultSort);
    }
    viewer.opener = opener;
    viewer.queued = 0;
    if (!dialog.open) {
      document.documentElement.classList.add('rawry-review-viewer-open');
      dialog.showModal();
      viewer.carried = true;
    }
    try {
      const select = nativeSort();
      if (select && select.value !== feed.defaultSort && !(await runBusy(() => setSort(feed.defaultSort)))) throw new Error('Could not load reviews');
      const index = feed.reviews.findIndex(review => review?.id === start.id);
      if (index < 0) throw new Error('Review not in the list');
      showReview(index, start.photo || 0, viewer.close);
    } catch (error) {
      viewer.opener = null;
      dialog.close();
      opener?.focus({ preventScroll: true });
      throw error;
    }
  }

  function scan() {
    document.querySelectorAll('.our-reviewyyyy').forEach(root => {
      const product = root.dataset.productTitle || '';
      root.querySelectorAll('.jdgm-rev-widg__header > .jdgm-row-carousel').forEach(row => enhanceRow(row, product));
    });
  }
  function watchRoots() {
    document.querySelectorAll('.our-reviewyyyy').forEach(root => {
      if (observed.has(root)) return;
      observed.add(root);
      new MutationObserver(scan).observe(root, { childList: true, subtree: true });
      // A double or triple click never highlights the section (David 2026-09-30); dragging still selects text.
      root.addEventListener('mousedown', event => {
        if (event.detail > 1 && !inField(event.target)) event.preventDefault();
      });
    });
    scan();
  }
  watchRoots();
  // The viewer is modal, so while it is open the arrow keys move review to review wherever focus is.
  document.addEventListener('keydown', event => {
    if (!viewer.dialog?.open || !arrowKey(event)) return;
    event.preventDefault();
    moveReview(event.key === 'ArrowRight' ? 1 : -1);
  });
  reducedMotion.addEventListener('change', () => tracks.forEach(applyMotion));
  // Judge.me builds the carousel after load; section replacement is editor-only.
  document.addEventListener('DOMContentLoaded', watchRoots, { once: true });
  document.addEventListener('shopify:section:load', watchRoots);
})();
